import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { TAGImage } from '../../../models/tagimage';
import { ApiService } from '../../api.service';
import { isPlatformServer } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { UseHttpImageSourcePipe } from '../../authimage.pipe';

interface MasonryItem {
  naturalWidth: number;
  naturalHeight: number;
  width: number;
  height: number;
  image: TAGImage;
  thumbLoaded: boolean;
  fullLoaded: boolean;
}

@Component({
  selector: 'masonry',
  imports: [SkeletonModule, UseHttpImageSourcePipe],
  providers: [],
  templateUrl: './masonry.html',
  styleUrl: './masonry.scss',
  host: {
    '(window:resize)': 'updateColumnCount()',
  },
})
export class Masonry {
  @ViewChild('masonryMain') masonryMain: ElementRef<HTMLElement>;
  private api = inject(ApiService);
  private platformId = inject(PLATFORM_ID);

  images = input<TAGImage[]>([]);
  loading = input<boolean>(false);
  useAuthImagePipe = input<boolean>(false);
  loadFullImages = input<boolean>(false);
  columnWidth = input<number>(400);
  actualColumnWidth = signal<number>(-1);

  isUpdating = signal(false);

  imageClicked = output<TAGImage>();

  columns = signal<number>(0);
  emptyArr = computed(() => new Array(this.columns()));

  columnItems = signal<MasonryItem[][]>(Array.from({ length: this.columns() }, () => []));

  getImageLink(imageid: string) {
    return this.api.getImageLink(imageid);
  }

  getThumbLink(imageid: string) {
    return this.api.getThumbLink(imageid);
  }

  updateColumnCount() {
    if (isPlatformServer(this.platformId)) return;
    if (!this.masonryMain) return;
    const rect = this.masonryMain.nativeElement.getBoundingClientRect();

    let columnFactor = rect.width / this.columnWidth();

    if (columnFactor < 1) this.actualColumnWidth.set(rect.width);
    else this.actualColumnWidth.set(this.columnWidth());

    this.columns.set(Math.max(Math.floor(columnFactor), 1));
    console.log('Size Calc: ', rect.width, this.actualColumnWidth(), this.columns());
  }

  constructor() {
    effect(() => {
      console.log('Columns: ' + this.columns());
    });

    effect(() => {
      if (isPlatformServer(this.platformId)) return;
      if (!this.images()) return;

      let cols = this.columns();
      let _actualColumnWidth = this.columnWidth();
      let _images = this.images();

      if (cols == 0) return;

      let updating = untracked(() => this.isUpdating());
      if (updating) {
        console.log('Is already updating');
        return;
      }

      this.isUpdating.set(true);

      let heights = new Array(cols).fill(0);
      this.columnItems.set(Array.from({ length: cols }, () => []));

      untracked(async () => {
        console.log(cols, heights, this.columnItems());

        const image = new Image();

        for (const item of _images) {
          const shortest = heights.indexOf(Math.min(...heights));

          let { width, height } = await new Promise<{ width: number; height: number }>(
            (resolve) => {
              image.onload = () => {
                resolve({ width: image.naturalWidth, height: image.naturalHeight });
              };
              image.onerror = () => {
                resolve({ width: 100, height: 100 });
              };
              image.src = <any>this.getThumbLink(item._id);
            },
          );

          let newImageHeight = (_actualColumnWidth / width) * height;

          this.columnItems.update((ci) => {
            ci[shortest] = [
              ...ci[shortest],
              {
                naturalWidth: width,
                naturalHeight: height,
                width: _actualColumnWidth,
                height: newImageHeight,
                image: item,
                thumbLoaded: false,
                fullLoaded: false,
              },
            ];

            return [...ci];
          });
          heights[shortest] += newImageHeight;
        }

        this.isUpdating.set(false);
      });
    });
  }

  ngAfterViewInit() {
    this.updateColumnCount();
  }

  setThumbLoaded(idx: number, imageID: string) {
    this.columnItems.update((ci) => {
      let index = ci[idx].findIndex((i) => i.image._id === imageID);
      ci[idx][index].thumbLoaded = true;
      return [...ci];
    });
  }

  setFullImageLoaded(idx: number, imageID: string) {
    this.columnItems.update((ci) => {
      let index = ci[idx].findIndex((i) => i.image._id === imageID);
      ci[idx][index].fullLoaded = true;
      return [...ci];
    });
  }
}
